import { Actions } from '#base/Canvas/Actions.js';
import { baseElementType, getDrawProductSteps, interruptibleRender } from '#base/Canvas/Canvas.js';
import { DesignerDispatch } from '#base/Canvas/Dispatch.js';
import { textBounds } from '#base/Text/textBounds.js';
import { useDebounce, useStateRef, useWait } from '#base/util/hook.js';
import { fabric } from 'fabric';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

const whyRedraw = false;
const _debugStroke = 10;
const debugColor = 'rgba(0,255,0,0.5)';
const imageSize = 'original';
const snapSections = 2; // how many sections x/y there should be for alignment
const snapDistance = 20;

const doRound = (n) => Math.round(n * 100) / 100;

// This is used to prevent flashing, especially during text editing. When a change happens,
// the redraw starts by clear()ing the fabric canvas, which not only resets the canvas
// state, but it also clears the HTML canvas. But it takes a little time to re-create the
// objects and add them back in before we do the renderAll(), during which time the render
// might be cancelled, since not infrequently we update something during the render
// (position or meta). This class queues all visible changes and does them all at once
// when renderAll is called.
class DeferredCanvas {
  CLEAR = 0;
  ADD = 1;
  SEND_TO_BACK = 2;
  SET_ACTIVE_OBJECT = 3;
  SET_ABSOLUTE_PAN = 4;
  SET_ZOOM = 5;
  SET_DIMENSIONS = 6;

  constructor(canvas, width, height) {
    this._canvas = canvas;
    this._commands = [];
    this._addedObjects = [];
    this._size = { width, height };
  }

  getFabricCanvas() {
    return this._canvas;
  }

  clear() {
    this._commands.push({ cmd: this.CLEAR, arg: null });
    this._addedObjects = [];
  }

  add(o) {
    this._commands.push({ cmd: this.ADD, arg: o });
    this._addedObjects.push(o);
  }

  sendToBack(o) {
    this._commands.push({ cmd: this.SEND_TO_BACK, arg: o });
  }

  setActiveObject(o) {
    this._commands.push({ cmd: this.SET_ACTIVE_OBJECT, arg: o });
  }

  absolutePan(o) {
    this._commands.push({ cmd: this.SET_ABSOLUTE_PAN, arg: o });
  }

  setZoom(z) {
    this._commands.push({ cmd: this.SET_ZOOM, arg: z });
  }

  setDimensions(size) {  // setDimensions() causes a re-render internal to fabric :(
    this._size = size;
    this._commands.push({ cmd: this.SET_DIMENSIONS, arg: size });
  }

  renderAll() {
    for (let cmd of this._commands) {
      switch (cmd.cmd) {
        case this.CLEAR:
          this._canvas.clear();
          break;
        case this.ADD:
          this._canvas.add(cmd.arg);
          break;
        case this.SEND_TO_BACK:
          this._canvas.sendToBack(cmd.arg);
          break;
        case this.SET_ACTIVE_OBJECT:
          this._canvas.setActiveObject(cmd.arg);
          break;
        case this.SET_ABSOLUTE_PAN:
          this._canvas.absolutePan(cmd.arg);
          break;
        case this.SET_ZOOM:
          this._canvas.setZoom(cmd.arg);
          break;
        case this.SET_DIMENSIONS:
          this._canvas.setDimensions(cmd.arg);
          break;
        default:
          console.error(`Unknown canvas command ${cmd.cmd}`);
          break;
      }
    }
    this._canvas.renderAll();
    this._commands = [];
  }

  set uniformScaling(s) {
    this._canvas.uniformScaling = s;
  }

  getWidth() {
    return this._size.width;
  }
  getHeight() {
    return this._size.height;
  }

  getActiveObjects() {
    return this._canvas.getActiveObjects();
  }
  getObjects(arg) {
    if (this._commands.length > 0) {
      if (arg) {
        return this._addedObjects.filter((o) => o.type === arg);
      }
      return this._addedObjects;
    }
    return this._canvas.getObjects(arg);
  }
  getRetinaScaling() {
    return this._canvas.getRetinaScaling();
  }
}

export const DesignCanvas = React.forwardRef(
  (
    {
      config,
      selectedFace,
      elements,
      selected,
      assetLibrary,
      canEdit,
      message,
      zoom,
      graphicType,
      snappy,
      mode,
      units = 'mm',
      nudge = 1,
      debug,
      isPreview = false,
    },
    forwardRef
  ) => {
    const dispatch = useContext(DesignerDispatch);
    const isMobile = useMediaQuery({ maxWidth: 767 });

    const rendering = useRef();
    const wrapperRef = useRef();
    const canvasRef = useRef();
    const [_isDrawing, setDrawing, getDrawing] = useStateRef(true);
    const [_readyToExport, setReadyToExport, getReadyToExport] = useStateRef(false);
    const [_waiting, setWaitFor] = useWait();

    const isZoomed = canEdit && zoom === 'design';

    const [[width, height, scale], setDims] = useState([600, 600, 1]);

    const ppmm = config.preview.width / config.item.width;

    const canvas = useMemo(() => {
      var canvas = new fabric.Canvas(canvasRef.current);
      // canvas.enableRetinaScaling = false;
      canvas.renderOnAddRemove = false;
      canvas.allowTouchScrolling = true;
      canvas.selection = false;
      return canvas;
    }, [canvasRef.current]);

    const [_itemBounds, designBounds, grid] = useMemo(() => {
      const itemBounds = {
        top: config.preview.offsetTop,
        left: config.preview.offsetLeft || width / 2 - config.preview.width / 2,
        height: config.preview.height,
        width: config.preview.width,
        fill: 'transparent',
        stroke: debugColor,
        strokeWidth: 2,
      };

      const _designBounds = {
        top: itemBounds.top + config.print.offsetTop * ppmm,
        left: itemBounds.left + (config.preview.width - config.print.width * ppmm) / 2,
        height: config.print.height * ppmm,
        width: config.print.width * ppmm,
        fill: 'transparent',
        stroke: debugColor,
        strokeWidth: 2,
        ppmm,
      };
      
      const inset = (config.print.inset || 0) * ppmm;

      const designBounds = Object.assign({}, _designBounds, {
        centerX: _designBounds.left + _designBounds.width / 2,
        centerY: _designBounds.top + _designBounds.height / 2,
        inset: {
          top: _designBounds.top + inset,
          left: _designBounds.left + inset,
          width: _designBounds.width - inset * 2,
          height: _designBounds.height - inset * 2,
        }
      });

      const getGrid = (size, sections) => {
        const sectionSize = size / sections;
        return Array.from({ length: sections + 1 }, (v, i) => {
          return i * sectionSize;
        });
      };

      const grid = {
        x: getGrid(designBounds.width, snapSections).map((v) => v + designBounds.left),
        y: getGrid(designBounds.height, snapSections).map((v) => v + designBounds.top),
      };

      const updateMask = (target) => {
        const isMulti = !!target.getObjects;
        const { x: tx, y: ty } = isMulti ? target.getCenterPoint() : { x: 0, y: 0 };
        const scale = isMulti ? target.scaleX : 1;
        const angle = isMulti ? target.angle : 0;
        const targets = isMulti ? target.getObjects() : [target];

        targets.forEach((o) => {
          if (o._mask) {
            const { x, y } = o.getCenterPoint();

            const radians = -angle * (Math.PI / 180);
            const cos = Math.cos(radians);
            const sin = Math.sin(radians);
            const nX = cos * x + sin * y;
            const nY = cos * y - sin * x;

            o._mask.clipPath.setPositionByOrigin(
              {
                y: ty + nY * scale,
                x: tx + nX * scale,
              },
              'center',
              'center'
            );

            o._mask.clipPath.set({
              scaleX: o.scaleX * scale,
              scaleY: o.scaleY * scale,
            });

            o._mask.clipPath.rotate(o.angle + angle);

            o._mask.set('dirty', true);
          }
        });
      };

      // only dispatch if user initiated
      canvas.off(); // clear all event handlers; on() appends onto a list, instead of replacing
      canvas.on('selection:created', ({ selected, e }) => {
        e &&
          dispatch(
            Actions.selectElements(
              config.face,
              selected.map((s) => s._elementId)
            )
          );
      });
      canvas.on('selection:updated', ({ selected, e }) => {
        e &&
          dispatch(
            Actions.selectElements(
              config.face,
              selected.map((s) => s._elementId)
            )
          );
      });
      canvas.on('selection:cleared', ({ e }) => {
        e && dispatch(Actions.selectElements(config.face, null));
      });
      const updateTarget = ({ target }) => {
        const targets = target.getObjects ? target.getObjects() : [target];
        const updates = targets.filter(t => !!t._elementId).map((t) => {
          const tm = t.calcTransformMatrix();

          const opt = fabric.util.qrDecompose(tm);
          const { translateX: left, translateY: top, scaleX: scale, angle: rotation } = opt;

          const x = (left - designBounds.centerX) / ppmm;
          const y = (top - designBounds.centerY) / ppmm;

          return {
            id: t._elementId,
            data: {
              x: doRound(x),
              y: doRound(y),
              scale: doRound(scale),
              rotation: doRound(rotation),
              o: 'cc',
              w: doRound(t._w / ppmm),
              h: doRound(t._h / ppmm),
            },
          };
        });

        if (updates.length > 0) {
          dispatch(Actions.positionElements(config.face, updates));
        }
      };
      canvas.on('object:added', updateTarget);
      canvas.on('object:modified', updateTarget);
      canvas.on('object:moving', ({ target }) => {
        // for the each edge and center of target (on each axis)
        // find the one closest to a grid line
        // test for snapping
        // set if appropriate

        const closestX = [...Array(3)].reduce((a, _, i) => {
          const offset = (target.width * i) / 2;
          const value = target.left + offset;

          const diff = grid.x.map((x) => Math.abs(value - x));
          const min = Math.min(...diff);
          const closest = grid.x[diff.indexOf(min)];

          if (typeof a.min === 'undefined' || a.min > min) {
            const snap = min <= snapDistance;
            a = {
              value,
              offset,
              closest,
              min,
              snap,
            };
          }
          return a;
        }, {});
        const snapX =
          snappy &&
          closestX?.snap &&
          Math.round(closestX.value / closestX.closest) * closestX.closest - closestX.offset;
        snapX && target.set({ left: snapX });

        const closestY = [...Array(3)].reduce((a, _, i) => {
          const offset = (target.height * i) / 2;
          const value = target.top + offset;

          const diff = grid.y.map((y) => Math.abs(value - y));
          const min = Math.min(...diff);
          const closest = grid.y[diff.indexOf(min)];

          if (typeof a.min === 'undefined' || a.min > min) {
            const snap = min <= snapDistance;
            a = {
              value,
              offset,
              closest,
              min,
              snap,
            };
          }
          return a;
        }, {});
        const snapY =
          snappy &&
          closestY?.snap &&
          Math.round(closestY.value / closestY.closest) * closestY.closest - closestY.offset;
        snapY && target.set({ top: snapY });

        if (snapX || snapY) {
          target.setCoords();
        }

        updateMask(target);
      });
      canvas.on('object:scaling', ({ target, transform }) => {
        if (transform.corner === 'br') {
          const left = transform.target.left;
          const width = transform.target.width * transform.target.scaleX;
          const x = left + width;
          const snapX = snappy && grid.x.find((g) => Math.abs(x - g) < snapDistance);

          const top = transform.target.top;
          const height = transform.target.height * transform.target.scaleY;
          const y = top + height;
          const snapY = snappy && grid.y.find((g) => Math.abs(y - g) < snapDistance);

          if (snapX || snapY) {
            if ((typeof snapY === 'undefined' && typeof snapX !== 'undefined') || snapX - x < snapY - y) {
              const scale = (snapX - left) / transform.target.width;
              target.scale(scale);
            } else {
              const scale = (snapY - top) / transform.target.height;
              target.scale(scale);
            }
          }
        } else {
          // TODO if scaling to the left need to recalculate the x position
        }

        const images = target.getObjects('image');
        if (images && images.length > 0) {
          const bitmapInfo = images[0].get('bitmapInfo');
          if (bitmapInfo) {
            const maxScale = bitmapInfo.maxScale / Math.min(images[0].scaleX, images[0].scaleY);
            target.scale(Math.min(target.scaleX, target.scaleY, maxScale));
          }
        }

        updateMask(target);
      });
      canvas.on('object:rotating', ({ target }) => {
        updateMask(target);
      });
      canvas.on('custom:delete', ({ target }) => {
        const targets = target.getObjects ? target.getObjects() : [target];
        const ids = targets.map((t) => t._elementId).filter((id) => !!id);
        dispatch(Actions.removeElements(config.face, ids));
      });
      canvas.on('custom:nudge', ({ x, y }) => {
        const active = canvas.getActiveObjects();
        if (active) {
          const _updates = active.map((t) => {
            const top = t.top + y * ppmm;
            const left = t.left + x * ppmm;
            t.set({ top, left });
            t.setCoords();
            return {
              id: t._elementId,
              data: {
                x: t.left + x,
                y: t.top + y,
              },
            };
          });
          canvas.fire('object:modified', {
            target: {
              getObjects: () => canvas.getActiveObjects(),
            },
          });
        }
      });

      return [itemBounds, designBounds, grid];
    }, [canvas, config, width, height, scale, snappy]);

    const handleKeyDown = useCallback(
      (e) => {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
          return;
        }

        let x = 0;
        let y = 0;

        switch (e.key) {
          case 'ArrowRight':
            x = 1;
            break;
          case 'ArrowLeft':
            x = -1;
            break;
          case 'ArrowUp':
            y = -1;
            break;
          case 'ArrowDown':
            y = 1;
            break;
          default:
            break;
        }

        if (x || y) {
          e.preventDefault();
          canvas.fire('custom:nudge', { x: x * nudge, y: y * nudge });
        }
      },
      [canvas, nudge]
    );

    useEffect(() => {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [handleKeyDown]);

    const sizeRef = useRef({ w: window.innerWidth, h: window.innerHeight });
    const [resized, setResized] = useState(0);
    const handleResized = useDebounce(() => {
      const o = sizeRef.current;
      const n = {
        w: window.innerWidth,
        h: window.innerHeight,
      };
      // we only care about width (on mobile height is constantly changing)
      if (n.w !== o.w) {
        setResized((v) => v + 1);
      }
      sizeRef.current = n;
    }, 10);

    useEffect(() => {
      window.addEventListener('resize', handleResized);
      return () => window.removeEventListener('resize', handleResized);
    }, []);

    const testRef = useRef({});
    useEffect(() => {
      if (whyRedraw) {
        const test = {
          wrapperRef,
          canvas,
          config,
          assetLibrary,
          designBounds,
          grid,
          elements,
          selected,
          width,
          height,
          zoom,
          setDims,
          dispatch,
          canEdit,
          resized,
          graphicType,
        };
        Object.keys(test).forEach((k) => {
          test[k] !== testRef.current[k] &&
            console.log('whyRedraw?', k, test[k] === testRef.current[k], {
              new: test[k],
              old: testRef.current[k],
            });
        });
        testRef.current = test;
      }

      setDrawing(true);
      setReadyToExport(false);

      const canvasWidth = isMobile ? 300 : wrapperRef.current.offsetWidth;
      const canvasHeight = isMobile ? 300 : wrapperRef.current.offsetHeight;
      const maxWidth = isZoomed && isMobile ? Math.min(200, canvasWidth) : canvasWidth;
      const maxHeight = isZoomed && isMobile ? Math.min(200, canvasHeight) : canvasHeight;

      const dispatchPositionElement = (face, id, x, y, o, w, h) => {
        dispatch(
          Actions.positionElement(face, id, {
            x: doRound(x),
            y: doRound(y),
            o: 'cc',
            w: doRound(w),
            h: doRound(h),
          })
        );
      };

      const afterRender = () => {
        if (!canEdit) return;

        // extract some positional info about elements to make sure they're all within designs bounds
        const db = {
          x1: designBounds.left,
          x2: designBounds.left + designBounds.width,
          y1: designBounds.top,
          y2: designBounds.top + designBounds.height,
        };

        const meta = canvas
          .getObjects()
          .filter((o) => !!o._elementId)
          .reduce((a, e) => {
            const eb = {
              x1: e.left,
              x2: e.left + e.width * e.scaleX,
              y1: e.top,
              y2: e.top + e.height * e.scaleY,
            };

            const meta = Object.assign({}, eb, {
              dl: eb.x1 - db.x1,
              dr: db.x2 - eb.x2,
              dt: eb.y1 - db.y1,
              db: db.y2 - eb.y2,
            });

            const element = elements.find((f) => f.get('id') === e._elementId);
            if (element && !!element.get('isGroup')) {
              const elementType = baseElementType(element.get('elementType'));
              const asset = assetLibrary[elementType].find((a) => a.key === element.get('assetId'));
              meta.gb = textBounds(element, asset);
            }

            meta.oob = !![meta.dl, meta.dr, meta.dt, meta.db].find((e) => e < 0);
            if (meta.oob && meta.gb) {
              // only concerned about x-axis out of bounds for group elements
              // TODO should we care about y-axis?
              const max = Math.max(...meta.gb.map((b) => b[0]));
              const width = meta.x2 - meta.x1;
              const scale = width / max;
              meta.gb = meta.gb.map((b) => {
                const offset = ((max - b[0]) / 2) * scale;
                const dl = meta.dl + offset;
                const dr = meta.dr + offset;
                const oob = dl < 0 || dr < 0;
                return {
                  dl,
                  dr,
                  oob,
                };
              });
            }

            a[e._elementId] = meta;

            return a;
          }, {});
        dispatch(Actions.updateMeta(config.face, meta));
      };

      const steps = getDrawProductSteps({
        canvas: new DeferredCanvas(canvas, maxWidth, maxHeight), // prevent flashing on edits
        width: maxWidth,
        height: maxHeight,
        imageSize,
        config,
        assetLibrary,
        designBounds,
        grid,
        elements,
        selected,
        isZoomed,
        isInteractive: true,
        canEdit,
        graphicType,
        mode,
        units,
        debug,
        isPreview,
        dispatchPositionElement,
        onBackgroundLoaded: (bg) => {
          const ratio = Math.min(maxWidth / bg.width, maxHeight / bg.height);
          if (ratio > 0 && (bg.width != width || bg.height != height || ratio != scale)) {
            setDims([bg.width, bg.height, ratio]);
          }
        },
      });

      const drawing = interruptibleRender(steps, null, () => {
        afterRender();
        setReadyToExport(true);
        setDrawing(false);
      });
      rendering.current = drawing;
      return async () => {
        (await drawing).cancel();
      };
    }, [
      wrapperRef,
      canvas,
      config,
      assetLibrary,
      designBounds,
      grid,
      elements,
      selected,
      width,
      height,
      zoom,
      setDims,
      dispatch,
      canEdit,
      resized,
      graphicType,
      isPreview,
      mode,
      units,
    ]);

    const exportCanvas = useMemo(
      () => () => {
        // export based on preview image size/aspect ratio, not current canvas

        const temp = document.createElement('canvas');
        const context = temp.getContext('2d');

        const scale = Math.min(
          canvasRef.current.width / config.preview.previewFile.width,
          canvasRef.current.height / config.preview.previewFile.height
        );

        temp.width = config.preview.previewFile.width * scale;
        temp.height = config.preview.previewFile.height * scale;

        const dx = (temp.width - canvasRef.current.width) / 2;
        const dy = (temp.height - canvasRef.current.height) / 2;

        context.drawImage(canvasRef.current, dx, dy);

        return temp.toDataURL('image/png');
      },
      [config, canvasRef, width, height, scale]
    );

    React.useImperativeHandle(
      forwardRef,
      () => ({
        export: () => {
          return new Promise((resolve) => {
            setWaitFor(
              () => getReadyToExport(),
              () => {
                resolve({ face: selectedFace, data: exportCanvas() });
              }
            );
          });
        },
      }),
      [exportCanvas, setDrawing, getDrawing, selectedFace]
    );

    return (
      <div ref={wrapperRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
        <canvas ref={canvasRef} {...{ width, height }} />
        {message && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              margin: 'auto',
              border: '1px solid #cccccc',
              borderRadius: '5px',
              width: '15em',
              height: '3em',
              lineHeight: '3em',
              textAlign: 'center',
              backgroundColor: 'white',
            }}>
            {message}
          </div>
        )}
      </div>
    );
  }
);
